package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_WALLPAPER_TOGGLE
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.trackDashboardCardClick
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON_DISPLAY
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_BG_WALLPAPER_SECTION
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_CIRCLE_DOWN
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_WOOD_CURVED
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_1_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_1_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_1_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_2_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_2_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_2_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_3_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_3_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_PREVIEW_3_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_SECTION_BUTTON
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_SECTION_DESCRIPTION
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_SECTION_LATEST_TEXT
import org.moxultimate.topwithmox.statics.DashboardStatics.WALLPAPER_SECTION_TITLE
import org.moxultimate.topwithmox.theme.FontFamily

/** Ordered list of wallpaper preview cards — 3 per row. */
private val WALLPAPER_PREVIEWS = listOf(
    Triple(WALLPAPER_PREVIEW_1_URL, WALLPAPER_PREVIEW_1_TITLE, WALLPAPER_PREVIEW_1_DESC),
    Triple(WALLPAPER_PREVIEW_2_URL, WALLPAPER_PREVIEW_2_TITLE, WALLPAPER_PREVIEW_2_DESC),
    Triple(WALLPAPER_PREVIEW_3_URL, WALLPAPER_PREVIEW_3_TITLE, WALLPAPER_PREVIEW_3_DESC),
)

/**
 * WallpaperSection
 * Dashboard section showcasing Top Free Wallpapers. Desktop: stacked full-width layout on a dark
 * starfield background. Mobile: same scroll-card pattern as ProductSection.
 */
@OptIn(DelicateApi::class)
@Composable
fun WallpaperSection() {
    val breakpoint = rememberBreakpoint()

    when (breakpoint) {
        Breakpoint.ZERO, Breakpoint.SM, Breakpoint.MD -> WallpaperSectionMobile()
        else -> WallpaperSectionDesktop()
    }
}

/**
 * WallpaperSectionDesktop
 * Dark starfield background with circle-down decoration at top and curved-wood at bottom.
 * Stacked content layout:
 * 1. Title (left, white) + CTA button (right) in a SpaceBetween row.
 * 2. Description text (white) spanning full width.
 * 3. "Latest Wallpapers" label left-aligned.
 * 4. 3-column flex-wrap grid of [WallpaperPreviewCard]s.
 */
@Composable
private fun WallpaperSectionDesktop() {
    val navigator = rememberAppNavigator()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_BG_WALLPAPER_SECTION')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        // Circle-down decoration at top center
        Image(
            src = IMG_CIRCLE_DOWN,
            modifier = Modifier
                .display(DisplayStyle.Block)
                .attrsModifier { attr("loading", "lazy") },
        )

        // Curved wood wave at the bottom — absolutely positioned behind content
        if (DASHBOARD_WALLPAPER_TOGGLE) {
            Image(
                src = IMG_WOOD_CURVED,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("position", "absolute")
                        property("bottom", "0")
                        property("left", "0")
                        property("z-index", "0")
                        property("object-fit", "cover")
                        property("display", "block")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        // Content overlay — z-index 1 keeps it above the wood wave
        Column(
            modifier = Modifier
                .width(clamp(min = 1100.px, value = 80.vw, max = 2000.px))
                .padding(top = 40.px, bottom = 160.px)
                .styleModifier { property("z-index", "1") },
        ) {
            // Title row — title left (white), button right
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 40.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                TitleText(title = WALLPAPER_SECTION_TITLE)
                GradientButton(
                    text = if (DASHBOARD_WALLPAPER_TOGGLE) WALLPAPER_SECTION_BUTTON else COMING_SOON,
                    buttonHeight = 50,
                    disabled = !DASHBOARD_WALLPAPER_TOGGLE,
                    onClick = { navigator.goToWallpaperPage() },
                )
            }

            DescriptionText(
                modifier = Modifier.padding(top = 16.px),
                description = WALLPAPER_SECTION_DESCRIPTION,
            )

            if (DASHBOARD_WALLPAPER_TOGGLE) {
                // "Latest Wallpapers" label — left-aligned above grid
                DescriptionText(
                    description = WALLPAPER_SECTION_LATEST_TEXT,
                    modifier = Modifier.padding(top = 80.px, bottom = 16.px),
                )

                // 3-column flex-wrap grid — 2 gaps of 24px = 48px, so calc((100% - 48px) / 3).
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier { property("flex-wrap", "wrap") },
                    horizontalArrangement = Arrangement.spacedBy(24.px),
                    verticalAlignment = Alignment.Top,
                ) {
                    WALLPAPER_PREVIEWS.forEach { (url, title, desc) ->
                        WallpaperPreviewCard(
                            imageUrl = url,
                            title = title,
                            description = desc,
                            onNavigate = { navigator.goToWallpaperPage() },
                            modifier = Modifier
                                .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                                .margin(bottom = 16.px),
                        )
                    }
                }
            } else {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 100.px, bottom = 50.px),
                    contentAlignment = Alignment.Center,
                ) {
                    TitleText(
                        title = COMING_SOON_DISPLAY,
                        modifier = Modifier.textAlign(TextAlign.Center),
                    )
                }
            }
        }
    }
}

/**
 * WallpaperPreviewCard
 * Clickable 16:9 wallpaper thumbnail with hover scale on image and hover opacity on text.
 * Navigates to the wallpaper page on click.
 *
 * @param imageUrl    Path or URL of the preview image.
 * @param title       Bold title shown below the image.
 * @param description Smaller subtitle shown below the title.
 * @param onNavigate  Called when the card is clicked — triggers wallpaper page navigation.
 * @param modifier    Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun WallpaperPreviewCard(
    imageUrl: String,
    title: String,
    description: String,
    onNavigate: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var isHovered by remember { mutableStateOf(false) }

    Column(
        modifier = modifier
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                onClick {
                    trackDashboardCardClick("wallpapers", title)
                    onNavigate()
                }
            }
            .styleModifier { property("cursor", "pointer") },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier { property("aspect-ratio", "16 / 9") },
        ) {
            Image(
                src = imageUrl,
                description = title,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("transition", "transform 0.3s ease, filter 0.3s ease")
                        property("transform", if (isHovered) "scale(1.06)" else "scale(1.0)")
                        property("filter", if (isHovered) "brightness(1.1)" else "brightness(1.0)")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        SpanText(
            text = title,
            modifier = Modifier
                .padding(top = 10.px)
                .color(Colors.White)
                .fontFamily(FontFamily)
                .fontSize(14.px)
                .fontWeight(FontWeight.Bold)
                .styleModifier {
                    property("display", "inline-block")
                    property("transition", "transform 0.2s ease")
                    property("transform", if (isHovered) "scale(1.12)" else "scale(1.0)")
                    property("transform-origin", "left center")
                },
        )

        if (description.isNotBlank()) {
            SpanText(
                text = description,
                modifier = Modifier
                    .padding(top = 4.px)
                    .color(Colors.White)
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal),
            )
        }
    }
}


//----- Wallpaper Section Mobile ---------------------------------------------------------------------------------------


/**
 * WallpaperSectionMobile
 * Dark starfield background: stacked title/description, then a horizontally scrollable card row,
 * then a CTA button — matching the ProductSection mobile pattern.
 */
@OptIn(DelicateApi::class)
@Composable
private fun WallpaperSectionMobile() {
    val navigator = rememberAppNavigator()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_BG_WALLPAPER_SECTION')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        // Circle-down decoration at top center
        Image(
            src = IMG_CIRCLE_DOWN,
            modifier = Modifier
                .display(DisplayStyle.Block)
                .attrsModifier { attr("loading", "lazy") },
        )

        // Content overlay
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 60.px, bottom = 40.px),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 40.px, bottom = 24.px, top = 30.px),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                TitleText(
                    title = WALLPAPER_SECTION_TITLE,
                    modifier = Modifier.textAlign(TextAlign.Center),
                )

                DescriptionText(
                    modifier = Modifier
                        .padding(top = 24.px)
                        .textAlign(TextAlign.Center),
                    description = WALLPAPER_SECTION_DESCRIPTION,
                )
            }

            if (DASHBOARD_WALLPAPER_TOGGLE) {
                DescriptionText(
                    description = WALLPAPER_SECTION_LATEST_TEXT,
                    modifier = Modifier.padding(left = 24.px, bottom = 12.px, top = 14.px),
                )

                // Horizontally scrollable row — each card is 85vw wide
                Row(
                    modifier = WallpaperCarouselStyle.toModifier()
                        .fillMaxWidth()
                        .padding(leftRight = 16.px),
                    horizontalArrangement = Arrangement.spacedBy(12.px),
                    verticalAlignment = Alignment.Top,
                ) {
                    WALLPAPER_PREVIEWS.forEach { (url, title, desc) ->
                        WallpaperPreviewCard(
                            imageUrl = url,
                            title = title,
                            description = desc,
                            onNavigate = { navigator.goToWallpaperPage() },
                            modifier = Modifier.styleModifier {
                                property("width", "85vw")
                                property("flex-shrink", "0")
                            },
                        )
                    }
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 34.px, bottom = 8.px),
                contentAlignment = Alignment.Center,
            ) {
                GradientButton(
                    text = if (DASHBOARD_WALLPAPER_TOGGLE) WALLPAPER_SECTION_BUTTON else COMING_SOON,
                    disabled = !DASHBOARD_WALLPAPER_TOGGLE,
                    onClick = { navigator.goToWallpaperPage() },
                )
            }
        }
    }
}


/**
 * WallpaperCarouselStyle
 * Horizontal-scroll container with hidden scrollbar across all browsers:
 * Firefox (scrollbar-width: none), IE/Edge (-ms-overflow-style: none),
 * Chrome/Safari (::-webkit-scrollbar { display: none }).
 */
val WallpaperCarouselStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("flex-wrap", "nowrap")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
    }
    cssRule("::-webkit-scrollbar") {
        Modifier.styleModifier {
            property("display", "none")
        }
    }
    // Prevent flex items from shrinking so each card keeps its natural size
    cssRule(" > *") {
        Modifier.styleModifier {
            property("flex-shrink", "0")
        }
    }
}
